import {
  EdgeSelectionPointerIcon,
  IconButton,
  IconButtonGroup,
  LoopSelectionPointerIcon,
  Tooltip,
} from '@frontend/legacy-components';

import { ViewerButtonTooltip } from 'modules/common/viewer/components';

import { granularityHotkeyLabel } from '../session/selectionGranularity';
import type { SelectionGranularity } from '../session/types';

/**
 * The two selection modes, and what each one takes.
 *
 * Ordered loop-first because loop selection is the default and the coarser of
 * the two — the group reads left to right as "whole shape, then one edge of it".
 */
const SELECTION_MODES: readonly {
  granularity: SelectionGranularity;
  label: string;
  detail?: string;
  Icon: typeof LoopSelectionPointerIcon;
}[] = [
  {
    granularity: 'loop',
    label: 'Select loops',
    Icon: LoopSelectionPointerIcon,
  },
  {
    granularity: 'edge',
    label: 'Select edges',
    Icon: EdgeSelectionPointerIcon,
  },
];

export type DxfSelectionModeGroupProps = {
  /** The session's stored granularity, so the active member is lit. */
  activeGranularity: SelectionGranularity;
  /**
   * Whether the granularity governs the next click at all.
   *
   * True under Choose and under every brush — both pick through the same
   * `pickAt`, so the mode decides what a brush paints as much as what Choose
   * selects. False only under Measure, which ignores geometry: neither button
   * lights there, because claiming a selection mode is active would be a lie.
   * The group stays usable either way — pressing one is the way back.
   */
  isInEffect: boolean;
  onSelectGranularity: (granularity: SelectionGranularity) => void;
  isDisabled?: boolean;
  /** Shown when the group is disabled — no drawing yet, or a locked file. */
  disabledReason?: string;
};

/**
 * Loop versus edge selection, as a radio group.
 *
 * Shaped after `DxfBrushToolGroup` so the two read as one control surface where
 * they sit side by side in the mapping toolbar, but it drives a different axis:
 * that group picks the *tool*, this one picks what a click by that tool takes.
 * Which is why both can be lit at once, and why this group is also shown while
 * viewing, where there are no brushes at all.
 *
 * The Alt modifier's temporary inversion is deliberately *not* reflected here.
 * It lasts only as long as the key is held, and a button flickering under a
 * modifier reads as the mode having changed when it has not — the cursor and the
 * hover accent already say what the next click will do.
 */
export const DxfSelectionModeGroup = ({
  activeGranularity,
  isInEffect,
  onSelectGranularity,
  isDisabled = false,
  disabledReason,
}: DxfSelectionModeGroupProps) => (
  <Tooltip
    placement="bottom"
    label={disabledReason}
    isDisabled={!isDisabled || disabledReason === undefined}
    shouldWrapChildren={isDisabled}
  >
    <IconButtonGroup
      size="md"
      borderWidth="1px"
      borderColor="separator.divider"
      borderStyle="solid"
      borderRadius="md"
    >
      {SELECTION_MODES.map(({ granularity, label, detail, Icon }) => {
        const isActive = isInEffect && activeGranularity === granularity;

        return (
          <Tooltip
            key={granularity}
            placement="bottom"
            label={
              <ViewerButtonTooltip
                firstLine={`${label} (${granularityHotkeyLabel(granularity)})`}
                secondLine={detail}
              />
            }
          >
            <IconButton
              aria-label={label}
              aria-pressed={isActive}
              isDisabled={isDisabled}
              onClick={() => onSelectGranularity(granularity)}
              px={1}
              m={0}
              borderRadius="3px"
              {...(isActive && {
                bg: 'interaction.active',
                color: 'feature.onInteraction',
                _hover: { bg: 'interaction.active' },
              })}
            >
              <Icon boxSize={3} color="inherit" />
            </IconButton>
          </Tooltip>
        );
      })}
    </IconButtonGroup>
  </Tooltip>
);
DxfSelectionModeGroup.displayName = 'DxfSelectionModeGroup';
